import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshCw, XCircle, Layers, Activity, Eye, CheckCircle, Clock, AlertCircle, Users, MousePointerClick, Search, Filter, AlertTriangle, ExternalLink, X, FileText, Mail, Globe, Server } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { AdminLayout } from '../../components/admin/AdminLayout';
import SystemHealthStatus from '../../components/admin/SystemHealthStatus';
import RecentErrorsTable from '../../components/admin/RecentErrorsTable';
import BulkActionsBar from '../../components/admin/BulkActionsBar';
import SendGridDeliverabilityIssues from '../../components/admin/SendGridDeliverabilityIssues';
import { supabase } from '../../lib/supabase';
import { adminMonitoringApi, adminEmailApi } from '../../lib/adminApi';

interface HealthStatus {
  failed_last_hour: number;
  failed_last_24h: number;
  failed_last_7d: number;
  last_failure_at: string | null;
  checked_at: string;
}

interface SystemError {
  id: string;
  created_at: string;
  recipient_email: string;
  email_type: string;
  subject: string;
  campaign_id?: string;
  user_id?: string;
  response_status_code?: number;
  error_message: string;
  request_payload?: any;
  response_body?: any;
  sendgrid_message_id?: string;
  user_email?: string;
  campaign_subject?: string;
  reviewed?: boolean;
  reviewed_at?: string;
  reviewed_by?: string;
}

interface BatchApiCall {
  id: string;
  batch_id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  user_email: string | null;
  batch_size: number;
  response_status_code: number | null;
  sendgrid_message_id: string | null;
  duration_ms: number | null;
  sent_at: string;
  full_request_payload: any;
  request_headers: any;
  response_headers: any;
  response_body: any;
}

interface SendGridActivityMessage {
  from_email: string;
  msg_id: string;
  subject: string;
  to_email: string;
  status: string;
  opens_count: number;
  clicks_count: number;
  last_event_time: string;
  local_data?: {
    id: string;
    campaign_id: string | null;
    user_id: string | null;
    email_type: string;
    created_at: string;
  } | null;
}

interface SendGridMessageDetail {
  sendgrid: {
    from_email: string;
    msg_id: string;
    subject: string;
    to_email: string;
    status: string;
    opens_count: number;
    clicks_count: number;
    last_event_time: string;
    outbound_ip: string;
    outbound_ip_type: string;
    teammate: string;
    api_key_id: string;
    asm_group_id: number | null;
    categories: string[];
    unique_args: Record<string, any>;
    events: Array<{
      event_name: string;
      processed: string;
      reason?: string;
      attempt_num?: number;
      url?: string;
      bounce_classification?: string;
      http_user_agent?: string;
      mx_server?: string;
    }>;
  };
  local: {
    id: string;
    campaign_id: string | null;
    user_id: string | null;
    email_type: string;
    subject: string;
    recipient_email: string;
    recipient_name: string | null;
    send_status: string | null;
    response_status_code: number | null;
    error_message: string | null;
    created_at: string;
    sent_at: string | null;
  } | null;
}

export function AdminEmailDebugger() {
  const navigate = useNavigate();
  const { authUser, user, isAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'errors' | 'batches' | 'sendgrid' | 'deliverability'>('errors');
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [systemErrors, setSystemErrors] = useState<SystemError[]>([]);
  const [errorsPagination, setErrorsPagination] = useState({ total: 0, limit: 50, offset: 0 });

  const [batchApiCalls, setBatchApiCalls] = useState<BatchApiCall[]>([]);
  const [tabsLoaded, setTabsLoaded] = useState<Record<string, boolean>>({ batches: false });

  const [selectedBatch, setSelectedBatch] = useState<BatchApiCall | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showReviewedErrors, setShowReviewedErrors] = useState(false);

  const [sendgridSearchEmail, setSendgridSearchEmail] = useState('');
  const [sendgridSearchResults, setSendgridSearchResults] = useState<SendGridActivityMessage[]>([]);
  const [sendgridSearchLoading, setSendgridSearchLoading] = useState(false);
  const [sendgridSearchError, setSendgridSearchError] = useState<string | null>(null);
  const [sendgridSearched, setSendgridSearched] = useState(false);

  const [selectedMessageDetail, setSelectedMessageDetail] = useState<SendGridMessageDetail | null>(null);
  const [showMessageDetailModal, setShowMessageDetailModal] = useState(false);
  const [messageDetailLoading, setMessageDetailLoading] = useState(false);
  const [messageDetailError, setMessageDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading) {
      if (!authUser) {
        navigate('/admin', { replace: true });
        return;
      }
      if (!isAdmin) {
        navigate('/dashboard', { replace: true });
        return;
      }
      loadData();
    }
  }, [authUser, isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (!authLoading && authUser && isAdmin) {
      loadSystemErrors(0);
    }
  }, [showReviewedErrors]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadHealthStatus(),
        loadSystemErrors(),
      ]);
      await loadTabData(activeTab);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
    setLoading(false);
  };

  const loadTabData = async (tab: 'errors' | 'batches' | 'sendgrid' | 'deliverability') => {
    if (tabsLoaded[tab]) return;

    try {
      switch (tab) {
        case 'batches':
          await loadBatchApiCalls();
          break;
        case 'sendgrid':
        case 'deliverability':
          break;
      }
      setTabsLoaded(prev => ({ ...prev, [tab]: true }));
    } catch (error) {
      console.error(`Failed to load ${tab} data:`, error);
    }
  };

  const loadHealthStatus = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      const result = await adminMonitoringApi.systemHealthStatus();
      setHealthStatus(result);
    } catch (error) {
      console.error('Failed to load health status:', error);
      setHealthStatus(null);
    }
  };

  const loadSystemErrors = async (offset = 0) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      const result = await adminMonitoringApi.recentSystemErrors({
        limit: errorsPagination.limit,
        offset,
        include_reviewed: showReviewedErrors,
      });
      setSystemErrors(result.data || []);
      setErrorsPagination({ total: result.total || 0, limit: errorsPagination.limit, offset });
    } catch (error) {
      console.error('Failed to load system errors:', error);
      setSystemErrors([]);
    }
  };

  const handleSendGridSearch = async () => {
    if (!sendgridSearchEmail.trim()) return;

    setSendgridSearchLoading(true);
    setSendgridSearchError(null);
    setSendgridSearched(true);

    try {
      const result = await adminEmailApi.searchSendGridActivity(sendgridSearchEmail.trim());
      setSendgridSearchResults(result.messages || []);
    } catch (error) {
      console.error('SendGrid search failed:', error);
      setSendgridSearchError(error instanceof Error ? error.message : 'Zoeken mislukt');
      setSendgridSearchResults([]);
    } finally {
      setSendgridSearchLoading(false);
    }
  };

  const handleViewMessageDetail = async (msgId: string) => {
    setMessageDetailLoading(true);
    setMessageDetailError(null);
    setShowMessageDetailModal(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      const response = await fetch(`${supabaseUrl}/functions/v1/sendgrid-message-detail?msg_id=${encodeURIComponent(msgId)}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setSelectedMessageDetail(data);
    } catch (error) {
      console.error('Failed to fetch message detail:', error);
      setMessageDetailError(error instanceof Error ? error.message : 'Details ophalen mislukt');
    } finally {
      setMessageDetailLoading(false);
    }
  };

  const loadBatchApiCalls = async () => {
    try {
      const { data, error } = await supabase
        .from('sendgrid_batch_api_calls')
        .select(`
          id,
          batch_id,
          campaign_id,
          batch_size,
          response_status_code,
          sendgrid_message_id,
          duration_ms,
          sent_at,
          email_campaigns!inner(subject),
          users!inner(email)
        `)
        .order('sent_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Failed to load batch API calls:', error);
        setBatchApiCalls([]);
        return;
      }

      const formattedData = (data || []).map((batch: any) => ({
        id: batch.id,
        batch_id: batch.batch_id,
        campaign_id: batch.campaign_id,
        campaign_name: batch.email_campaigns?.subject || null,
        user_email: batch.users?.email || null,
        batch_size: batch.batch_size,
        response_status_code: batch.response_status_code,
        sendgrid_message_id: batch.sendgrid_message_id,
        duration_ms: batch.duration_ms,
        sent_at: batch.sent_at,
        full_request_payload: null,
        request_headers: null,
        response_headers: null,
        response_body: null,
      }));

      setBatchApiCalls(formattedData);
    } catch (error) {
      console.error('Exception loading batch API calls:', error);
      setBatchApiCalls([]);
    }
  };



  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleViewErrors = () => {
    setActiveTab('errors');
  };

  const handleLoadMoreErrors = async () => {
    const newOffset = errorsPagination.offset + errorsPagination.limit;
    await loadSystemErrors(newOffset);
  };

  const handleMarkReviewed = async (errorId: string, reviewed: boolean) => {
    try {
      const { error } = await supabase
        .from('email_send_logs')
        .update({
          reviewed,
          reviewed_at: reviewed ? new Date().toISOString() : null,
          reviewed_by: reviewed ? user?.id : null,
        })
        .eq('id', errorId);

      if (error) throw error;

      setSystemErrors(prev =>
        prev.map(err =>
          err.id === errorId
            ? {
                ...err,
                reviewed,
                reviewed_at: reviewed ? new Date().toISOString() : undefined,
                reviewed_by: reviewed ? user?.id : undefined,
              }
            : err
        )
      );
    } catch (error) {
      console.error('Failed to mark error as reviewed:', error);
      throw error;
    }
  };

  const handleBulkAction = async (
    action: 'mark_visible' | 'mark_by_campaign' | 'mark_by_timeframe',
    params: any
  ) => {
    try {
      const result = await adminEmailApi.bulkReview({
        action,
        ...params,
        reviewed: true,
      });

      console.log('Bulk action result:', result);

      // Show success toast
      alert(
        `Succesvol ${result.affected_count} email(s) gemarkeerd als bekeken.`
      );

      // Reload errors to reflect changes
      await loadSystemErrors();
    } catch (error) {
      console.error('Bulk action failed:', error);
      throw error;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getSendGridStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      'delivered': 'bg-green-100 text-green-700',
      'processed': 'bg-blue-100 text-blue-700',
      'not_delivered': 'bg-red-100 text-red-700',
      'bounce': 'bg-red-100 text-red-700',
      'deferred': 'bg-yellow-100 text-yellow-700',
    };

    const icons: Record<string, React.ReactNode> = {
      'delivered': <CheckCircle className="w-3 h-3" />,
      'processed': <Activity className="w-3 h-3" />,
      'not_delivered': <XCircle className="w-3 h-3" />,
      'bounce': <XCircle className="w-3 h-3" />,
      'deferred': <Clock className="w-3 h-3" />,
    };

    const labels: Record<string, string> = {
      'delivered': 'Afgeleverd',
      'processed': 'Verwerkt',
      'not_delivered': 'Niet afgeleverd',
      'bounce': 'Bounce',
      'deferred': 'Uitgesteld',
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {icons[status]}
        {labels[status] || status}
      </span>
    );
  };

  const getEventBadge = (eventName: string) => {
    const eventStyles: Record<string, string> = {
      'processed': 'bg-blue-100 text-blue-700 border-blue-200',
      'delivered': 'bg-green-100 text-green-700 border-green-200',
      'open': 'bg-teal-100 text-teal-700 border-teal-200',
      'click': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'bounce': 'bg-red-100 text-red-700 border-red-200',
      'dropped': 'bg-red-100 text-red-700 border-red-200',
      'deferred': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'spamreport': 'bg-orange-100 text-orange-700 border-orange-200',
      'unsubscribe': 'bg-gray-100 text-gray-700 border-gray-200',
    };

    const eventLabels: Record<string, string> = {
      'processed': 'Verwerkt',
      'delivered': 'Afgeleverd',
      'open': 'Geopend',
      'click': 'Geklikt',
      'bounce': 'Bounce',
      'dropped': 'Gedropt',
      'deferred': 'Uitgesteld',
      'spamreport': 'Spam',
      'unsubscribe': 'Uitgeschreven',
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${eventStyles[eventName] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
        {eventLabels[eventName] || eventName}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Monitoring</h1>
            <p className="text-sm text-gray-600 mt-1">
              Monitor systeem gezondheid en bekijk email verzendingen
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Vernieuwen
          </button>
        </div>
      </div>

      <div className="mb-6">
        <SystemHealthStatus
          status={healthStatus}
          loading={loading}
          onViewErrors={handleViewErrors}
        />
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-2">
          <button
            onClick={() => setActiveTab('errors')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'errors'
                ? 'border-[#E94A47] text-[#E94A47]'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <XCircle className="w-4 h-4" />
            Recente Fouten
            {!showReviewedErrors && errorsPagination.total > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                activeTab === 'errors' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {errorsPagination.total}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('batches');
              loadTabData('batches');
            }}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'batches'
                ? 'border-[#E94A47] text-[#E94A47]'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <Layers className="w-4 h-4" />
            Batch API Calls
          </button>
          <button
            onClick={() => setActiveTab('sendgrid')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'sendgrid'
                ? 'border-[#E94A47] text-[#E94A47]'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <Search className="w-4 h-4" />
            SendGrid Zoeken
          </button>
          <button
            onClick={() => {
              setActiveTab('deliverability');
              loadTabData('deliverability');
            }}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'deliverability'
                ? 'border-[#E94A47] text-[#E94A47]'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            SendGrid Issues
          </button>
        </nav>
      </div>

      {activeTab === 'errors' && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Recente Systeem Fouten
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Emails die niet verzonden konden worden vanwege technische problemen in ons systeem.
                </p>
              </div>
              <button
                onClick={() => setShowReviewedErrors(!showReviewedErrors)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                  showReviewedErrors
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                <Filter className="w-4 h-4" />
                {showReviewedErrors ? 'Alle fouten' : 'Alleen onbekeken'}
              </button>
            </div>

            <BulkActionsBar
              errors={systemErrors}
              onBulkAction={handleBulkAction}
              disabled={loading}
            />

            <RecentErrorsTable
              errors={systemErrors}
              loading={loading}
              onLoadMore={handleLoadMoreErrors}
              hasMore={errorsPagination.offset + errorsPagination.limit < errorsPagination.total}
              onMarkReviewed={handleMarkReviewed}
            />
          </div>
        </div>
      )}

      {activeTab === 'batches' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Batch API Calls ({batchApiCalls.length})
            </h2>
          </div>

          {batchApiCalls.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Geen batch API calls gevonden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Datum/Tijd</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Batch ID</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Campagne</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Ontvangers</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">HTTP</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Duur</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {batchApiCalls.map((batch) => (
                    <tr key={batch.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {formatDateTime(batch.sent_at)}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono text-gray-700 max-w-xs truncate">
                        {batch.batch_id}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700 max-w-xs truncate">
                        {batch.campaign_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          <Users className="w-3 h-3" />
                          {batch.batch_size}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-mono">
                        <span className={batch.response_status_code && batch.response_status_code >= 200 && batch.response_status_code < 300 ? 'text-green-600' : 'text-red-600'}>
                          {batch.response_status_code || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-mono text-gray-700">
                        {formatDuration(batch.duration_ms)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={async () => {
                            const { data } = await supabase
                              .from('sendgrid_batch_api_calls')
                              .select('full_request_payload, request_headers, response_headers, response_body')
                              .eq('id', batch.id)
                              .single();

                            setSelectedBatch({
                              ...batch,
                              full_request_payload: data?.full_request_payload,
                              request_headers: data?.request_headers,
                              response_headers: data?.response_headers,
                              response_body: data?.response_body,
                            });
                            setShowBatchModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1 mx-auto"
                        >
                          <Eye className="w-4 h-4" />
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'sendgrid' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200 space-y-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">SendGrid Email Activity</h2>
              <p className="text-sm text-gray-600 mt-1">
                Zoek direct in SendGrid naar email activiteit voor een specifiek emailadres.
              </p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendGridSearch();
              }}
              className="flex gap-3"
            >
              <div className="relative flex-1">
                <input
                  type="email"
                  placeholder="Voer een emailadres in om te zoeken..."
                  value={sendgridSearchEmail}
                  onChange={(e) => setSendgridSearchEmail(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              <button
                type="submit"
                disabled={sendgridSearchLoading || !sendgridSearchEmail.trim()}
                className="px-6 py-2 bg-[#E94A47] text-white rounded-lg hover:bg-[#d43f3c] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sendgridSearchLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Zoeken...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Zoeken
                  </>
                )}
              </button>
            </form>
          </div>

          {sendgridSearchError && (
            <div className="p-4 bg-red-50 border-b border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">{sendgridSearchError}</p>
                  {sendgridSearchError.includes('Email Activity API access not enabled') && (
                    <p className="text-xs text-red-600 mt-1">
                      De SendGrid Email Activity API vereist een betaald plan met Email Activity Feed.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {!sendgridSearched && !sendgridSearchLoading && (
            <div className="text-center py-16">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-2">Zoek naar email activiteit</p>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                Voer een emailadres in om de verzendgeschiedenis en status direct vanuit SendGrid op te halen.
              </p>
            </div>
          )}

          {sendgridSearched && !sendgridSearchLoading && !sendgridSearchError && sendgridSearchResults.length === 0 && (
            <div className="text-center py-16">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">
                Geen email activiteit gevonden voor "{sendgridSearchEmail}"
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Dit kan betekenen dat er geen emails naar dit adres zijn verzonden in de afgelopen 7 dagen.
              </p>
            </div>
          )}

          {sendgridSearchResults.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Laatste Event</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Onderwerp</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Opens</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Clicks</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Lokale Data</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {sendgridSearchResults.map((msg, idx) => (
                    <tr key={msg.msg_id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {formatDateTime(msg.last_event_time)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700 max-w-xs truncate" title={msg.subject}>
                        {msg.subject || '-'}
                      </td>
                      <td className="py-3 px-4">
                        {getSendGridStatusBadge(msg.status)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {msg.opens_count > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <Eye className="w-3 h-3" />
                            {msg.opens_count}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {msg.clicks_count > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            <MousePointerClick className="w-3 h-3" />
                            {msg.clicks_count}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {msg.local_data ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {msg.local_data.email_type}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Niet gekoppeld</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleViewMessageDetail(msg.msg_id)}
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1 mx-auto"
                        >
                          <FileText className="w-4 h-4" />
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <ExternalLink className="w-4 h-4" />
              <span>Data komt direct van de SendGrid Email Activity API (laatste 7 dagen)</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'deliverability' && (
        <SendGridDeliverabilityIssues />
      )}

      {showBatchModal && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Batch API Call Details</h3>
                  <p className="text-sm text-gray-600 mt-1">Batch ID: {selectedBatch.batch_id}</p>
                </div>
                <button
                  onClick={() => setShowBatchModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-600 mb-2">Algemene Informatie</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <dl className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-gray-600 mb-1">Batch Size:</dt>
                      <dd className="font-mono">{selectedBatch.batch_size}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600 mb-1">HTTP Status:</dt>
                      <dd className={`font-mono font-semibold ${selectedBatch.response_status_code && selectedBatch.response_status_code >= 200 && selectedBatch.response_status_code < 300 ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedBatch.response_status_code || '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-600 mb-1">Campagne:</dt>
                      <dd className="font-medium">{selectedBatch.campaign_name || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600 mb-1">Verzonden op:</dt>
                      <dd className="font-medium">{formatDateTime(selectedBatch.sent_at)}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {selectedBatch.full_request_payload && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">Request Body (Payload naar SendGrid)</h4>
                  <div className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto max-h-96">
                    <pre className="text-xs font-mono whitespace-pre-wrap">
                      {JSON.stringify(selectedBatch.full_request_payload, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {selectedBatch.response_body && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">Response Body</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                      {JSON.stringify(selectedBatch.response_body, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6">
              <button
                onClick={() => setShowBatchModal(false)}
                className="btn-primary w-full"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {showMessageDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">SendGrid Message Details</h3>
                  {selectedMessageDetail && (
                    <p className="text-sm text-gray-600 mt-1 font-mono truncate max-w-xl">
                      {selectedMessageDetail.sendgrid.msg_id}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowMessageDetailModal(false);
                    setSelectedMessageDetail(null);
                    setMessageDetailError(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {messageDetailLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <LoadingSpinner />
                    <p className="text-gray-600 mt-4">Details ophalen van SendGrid...</p>
                  </div>
                </div>
              )}

              {messageDetailError && !messageDetailLoading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800">Fout bij ophalen details</h4>
                      <p className="text-sm text-red-700 mt-1">{messageDetailError}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedMessageDetail && !messageDetailLoading && !messageDetailError && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Mail className="w-5 h-5 text-gray-600" />
                        <h4 className="font-semibold text-gray-900">Email Informatie</h4>
                      </div>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Van:</dt>
                          <dd className="font-medium text-gray-900">{selectedMessageDetail.sendgrid.from_email}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Naar:</dt>
                          <dd className="font-medium text-gray-900">{selectedMessageDetail.sendgrid.to_email}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Status:</dt>
                          <dd>{getSendGridStatusBadge(selectedMessageDetail.sendgrid.status)}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Opens:</dt>
                          <dd className="font-medium">{selectedMessageDetail.sendgrid.opens_count || 0}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Clicks:</dt>
                          <dd className="font-medium">{selectedMessageDetail.sendgrid.clicks_count || 0}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Server className="w-5 h-5 text-gray-600" />
                        <h4 className="font-semibold text-gray-900">Technische Details</h4>
                      </div>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Outbound IP:</dt>
                          <dd className="font-mono text-gray-900">{selectedMessageDetail.sendgrid.outbound_ip || '-'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">IP Type:</dt>
                          <dd className="font-medium">{selectedMessageDetail.sendgrid.outbound_ip_type || '-'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-600">API Key ID:</dt>
                          <dd className="font-mono text-xs">{selectedMessageDetail.sendgrid.api_key_id || '-'}</dd>
                        </div>
                        {selectedMessageDetail.sendgrid.asm_group_id && (
                          <div className="flex justify-between">
                            <dt className="text-gray-600">ASM Group:</dt>
                            <dd className="font-medium">{selectedMessageDetail.sendgrid.asm_group_id}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-gray-600" />
                      <h4 className="font-semibold text-gray-900">Onderwerp</h4>
                    </div>
                    <p className="text-gray-900">{selectedMessageDetail.sendgrid.subject || '-'}</p>
                  </div>

                  {selectedMessageDetail.sendgrid.categories && selectedMessageDetail.sendgrid.categories.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Categories</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedMessageDetail.sendgrid.categories.map((cat, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedMessageDetail.sendgrid.unique_args && Object.keys(selectedMessageDetail.sendgrid.unique_args).length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Custom Arguments</h4>
                      <div className="bg-gray-50 rounded p-3 overflow-x-auto">
                        <pre className="text-xs font-mono text-gray-800">
                          {JSON.stringify(selectedMessageDetail.sendgrid.unique_args, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {selectedMessageDetail.sendgrid.events && selectedMessageDetail.sendgrid.events.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Activity className="w-5 h-5 text-gray-600" />
                        <h4 className="font-semibold text-gray-900">Event Timeline</h4>
                        <span className="text-sm text-gray-500">({selectedMessageDetail.sendgrid.events.length} events)</span>
                      </div>
                      <div className="space-y-3">
                        {selectedMessageDetail.sendgrid.events.map((event, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-shrink-0 pt-0.5">
                              {getEventBadge(event.event_name)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="w-4 h-4" />
                                {formatDateTime(event.processed)}
                              </div>
                              {event.reason && (
                                <p className="text-sm text-red-600 mt-1">
                                  <span className="font-medium">Reden:</span> {event.reason}
                                </p>
                              )}
                              {event.bounce_classification && (
                                <p className="text-sm text-orange-600 mt-1">
                                  <span className="font-medium">Bounce type:</span> {event.bounce_classification}
                                </p>
                              )}
                              {event.mx_server && (
                                <p className="text-sm text-gray-600 mt-1">
                                  <span className="font-medium">MX Server:</span>{' '}
                                  <span className="font-mono text-xs">{event.mx_server}</span>
                                </p>
                              )}
                              {event.url && (
                                <p className="text-sm text-blue-600 mt-1 truncate">
                                  <span className="font-medium text-gray-600">URL:</span>{' '}
                                  <a href={event.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                    {event.url}
                                  </a>
                                </p>
                              )}
                              {event.http_user_agent && (
                                <p className="text-xs text-gray-500 mt-1 truncate" title={event.http_user_agent}>
                                  <Globe className="w-3 h-3 inline mr-1" />
                                  {event.http_user_agent}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex-shrink-0">
              <button
                onClick={() => {
                  setShowMessageDetailModal(false);
                  setSelectedMessageDetail(null);
                  setMessageDetailError(null);
                }}
                className="btn-primary w-full"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
}
